import { useEffect, useState, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { BrandLogo } from '../components/BrandLogo';
import { Mail, FileUp, Check, ArrowLeft, ArrowRight, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { BDAFDots, tierFor } from '../components/BDAFDots';
import { SIGNAL_ICONS } from '../components/BDAFAudit';

const STEPS = ['Found', 'Verify', 'Claim', 'Enrich', 'Ranked'];
const ENRICH = [
  { key: 'website', label: 'Add your website' },
  { key: 'google_business', label: 'Connect Google Business Profile' },
  { key: 'directory_listing', label: 'Add to 3 directories' },
  { key: 'social_media', label: 'Add social media profile' },
  { key: 'reviews', label: 'Get your first customer review' },
];

function Stepper({ current }) {
  return (
    <ol className="flex items-center gap-2 sm:gap-3 flex-wrap" data-testid="claim-stepper">
      {STEPS.map((s, i) => {
        const n = i + 1; const done = n < current; const active = n === current;
        return (
          <li key={s} className="flex items-center gap-2">
            <span className={`w-7 h-7 rounded-full flex items-center justify-center font-body text-xs font-bold border transition-colors ${done ? 'bg-forest text-white border-forest' : active ? 'bg-brand-orange text-white border-brand-orange' : 'bg-white text-mid border-warm-border'}`}>
              {done ? <Check className="w-3.5 h-3.5" strokeWidth={3} /> : n}
            </span>
            <span className={`font-body text-xs font-medium ${active ? 'text-ink' : 'text-mid'}`}>{s}</span>
            {i < STEPS.length - 1 && <span className="w-4 sm:w-8 h-px bg-warm-border" />}
          </li>
        );
      })}
    </ol>
  );
}

function BizHeader({ biz }) {
  const t = tierFor(biz.bdaf_score);
  return (
    <div className="flex items-start gap-4">
      <span className="w-12 h-12 rounded-[14px] bg-bg-warm flex items-center justify-center shrink-0"><Building2 className="w-5 h-5 text-ink-2" strokeWidth={1.75} /></span>
      <div className="flex-1 min-w-0">
        <h1 className="font-display text-xl sm:text-2xl font-bold text-ink leading-tight" data-testid="claim-business-name">{biz.company_name}</h1>
        <p className="font-body text-xs text-mid mt-1">{biz.postcode} · {biz.sector}{biz.established ? ` · Est. ${String(biz.established).slice(-4)}` : ''}{biz.company_number ? ` · Co. No. ${biz.company_number}` : ''}</p>
      </div>
      <div className="text-right shrink-0">
        <p className="font-body text-[11px] uppercase tracking-wider text-mid">Score</p>
        <p className="font-display text-2xl font-extrabold" style={{ color: t.color }}>{biz.bdaf_score}</p>
      </div>
    </div>
  );
}

export default function ClaimPage() {
  const { id } = useParams();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [biz, setBiz] = useState(null);
  const [step, setStep] = useState(1);
  const [method, setMethod] = useState('email');
  const [claim, setClaim] = useState(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [file, setFile] = useState(null);
  const fileRef = useRef();

  useEffect(() => {
    const src = new URLSearchParams(window.location.search).get('src');
    if (src) sessionStorage.setItem('adoptic_src', src);
    if (!loading && !user) { navigate(`/login?next=${encodeURIComponent(`/claim/${id}${src ? `?src=${src}` : ''}`)}`, { replace: true }); return; }
    if (loading) return;
    api.get(`/claims/business/${id}`).then(r => {
      setBiz(r.data);
      if (r.data.is_mine) setStep(3);
    }).catch(() => toast.error('Business not found'));
  }, [id, user, loading, navigate]);

  const requireLogin = () => {
    if (!user) { navigate(`/login?next=/claim/${id}`); return false; }
    return true;
  };

  const startVerify = async () => {
    if (!requireLogin()) return;
    setBusy(true);
    try {
      const r = await api.post('/claims', { business_id: biz.id, method, source: sessionStorage.getItem('adoptic_src') || 'app' });
      setClaim(r.data);
      if (r.data.verification_status === 'pending' || r.data.verification_status === 'verified') { setStep(3); return; }
      if (method === 'email') toast.success(`Verification code sent to ${user.email}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not start claim'); }
    finally { setBusy(false); }
  };

  const submitCode = async () => {
    setBusy(true);
    try { await api.post(`/claims/${claim.id}/verify-code`, { code }); setStep(3); toast.success('Profile claimed — pending admin verification'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Incorrect code'); }
    finally { setBusy(false); }
  };

  const submitDoc = async () => {
    if (!file) return toast.error('Choose a PDF, PNG or JPG');
    setBusy(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      await api.post(`/claims/${claim.id}/document`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setStep(3); toast.success('Document received — pending admin verification');
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    finally { setBusy(false); }
  };

  if (!biz) return <div className="min-h-screen flex items-center justify-center font-body text-sm text-mid" data-testid="claim-loading">{loading ? 'Loading…' : 'Loading business…'}</div>;
  const t = tierFor(biz.bdaf_score);
  const gaps = (biz.signals || []).filter(s => !s.detected);

  return (
    <div className="min-h-screen bg-page-bg" data-testid="claim-page">
      <header className="h-16 border-b border-warm-border bg-white/80 backdrop-blur-md flex items-center">
        <div className="max-w-[1200px] mx-auto px-4 lg:px-6 w-full flex items-center justify-between">
          <BrandLogo testId="claim-brand" />
          <span className="font-body text-xs text-mid">{user ? user.email : <Link to={`/login?next=/claim/${id}`} className="text-brand-orange font-medium">Sign in</Link>}</span>
        </div>
      </header>

      <main className="max-w-[720px] mx-auto px-4 lg:px-6 py-10 space-y-6">
        <div className="solid-card p-6 sm:p-8 space-y-6 animate-fade-in">
          <BizHeader biz={biz} />
          <Stepper current={step} />

          {step === 1 && (
            <div className="space-y-5" data-testid="claim-step-1">
              <p className="font-body text-[15px] text-ink-2 leading-relaxed">We found this business in the Adoptic directory. It currently scores <b className="text-ink">{biz.bdaf_score} out of 5</b>{biz.bdaf_score <= 1 ? ' — meaning almost no customers can find it online.' : '.'} If you own or manage this business, claim this profile for free and take control of how it appears.</p>
              {gaps.length > 0 && (
                <div className="rounded-[14px] p-4 border" style={{ background: t.bg, borderColor: t.border }}>
                  <p className="font-body text-sm font-semibold" style={{ color: t.color }}>{biz.bdaf_score <= 1 ? 'This business is currently invisible.' : `This business is ${t.label.toLowerCase()}.`}</p>
                  <p className="font-body text-xs text-ink-2 mt-1">{gaps.map(g => `No ${g.label.toLowerCase()}`).join('. ')}.</p>
                </div>
              )}
              <div className="flex items-center justify-between gap-3 flex-wrap pt-2">
                <p className="font-body text-sm font-semibold text-ink">This is your business?</p>
                <div className="flex gap-2">
                  <Link to="/" className="px-4 py-2 rounded-full border border-warm-border font-body text-sm text-ink-2 hover:bg-bg-warm transition-colors" data-testid="claim-not-mine">Not my business</Link>
                  <button onClick={() => requireLogin() && setStep(2)} className="px-4 py-2 rounded-full bg-brand-orange text-white font-body text-sm font-semibold hover:bg-highlight-orange transition-colors inline-flex items-center gap-1" data-testid="claim-yes-mine">Yes, I own this <ArrowRight className="w-3.5 h-3.5" /></button>
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5" data-testid="claim-step-2">
              <div>
                <h2 className="font-display text-lg font-bold text-ink">Verify you are authorised to manage this profile</h2>
                <p className="font-body text-sm text-ink-2 mt-1">To protect businesses from unauthorised claims, every claim is reviewed by the Adoptic team. Choose your verification method:</p>
              </div>
              {!claim && (
                <div className="space-y-3">
                  {[
                    { id: 'email', icon: Mail, title: 'Email verification code', desc: `We send a 6-digit code to ${user?.email || 'your work email'}, then our team reviews the claim.`, tag: 'Fastest' },
                    { id: 'document', icon: FileUp, title: 'Upload a document', desc: 'Upload a Companies House confirmation statement or utility bill showing the business address. Reviewed within 2 business days.' },
                  ].map(o => (
                    <button key={o.id} onClick={() => setMethod(o.id)} data-testid={`claim-method-${o.id}`}
                      className={`w-full text-left p-4 rounded-[14px] border transition-all flex items-start gap-3 ${method === o.id ? 'border-brand-orange bg-accent-light' : 'border-warm-border bg-white hover:border-soft-grey'}`}>
                      <span className={`w-9 h-9 rounded-[10px] flex items-center justify-center shrink-0 ${method === o.id ? 'bg-brand-orange text-white' : 'bg-bg-warm text-ink-2'}`}><o.icon className="w-4 h-4" /></span>
                      <span className="flex-1">
                        <span className="font-body text-sm font-semibold text-ink flex items-center gap-2">{o.title}{o.tag && <span className="px-1.5 py-0.5 rounded-full bg-forest-light text-forest text-[10px] font-bold">{o.tag}</span>}</span>
                        <span className="font-body text-xs text-mid block mt-0.5">{o.desc}</span>
                      </span>
                    </button>
                  ))}
                </div>
              )}
              {claim?.verification_status === 'awaiting_code' && (
                <div className="space-y-3" data-testid="claim-code-entry">
                  <label className="font-body text-xs font-semibold text-ink-2 uppercase tracking-wider">Enter the 6-digit code</label>
                  <input value={code} onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} inputMode="numeric" placeholder="••••••" data-testid="claim-code-input"
                    className="w-full h-12 rounded-[10px] border border-warm-border bg-white px-4 font-body text-2xl tracking-[0.4em] text-center focus:outline-none focus:ring-2 focus:ring-brand-orange/40 focus:border-brand-orange" />
                  <p className="font-body text-xs text-mid">Sent to {user?.email}. Didn't get it? <button onClick={startVerify} className="text-brand-orange font-medium">Resend</button></p>
                </div>
              )}
              {claim?.verification_status === 'awaiting_document' && (
                <div className="space-y-3" data-testid="claim-doc-entry">
                  <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg" className="hidden" onChange={e => setFile(e.target.files?.[0] || null)} data-testid="claim-doc-input" />
                  <button onClick={() => fileRef.current?.click()} className="w-full p-6 rounded-[14px] border-2 border-dashed border-warm-border hover:border-brand-orange bg-white transition-colors font-body text-sm text-ink-2" data-testid="claim-doc-choose">
                    {file ? <span className="font-semibold text-ink">{file.name}</span> : 'Click to choose a PDF, PNG or JPG (max 10MB)'}
                  </button>
                </div>
              )}
              <div className="flex items-center justify-between pt-2">
                <button onClick={() => { setStep(1); setClaim(null); }} className="inline-flex items-center gap-1 font-body text-sm text-mid hover:text-ink" data-testid="claim-back"><ArrowLeft className="w-3.5 h-3.5" /> Back</button>
                {!claim && <button onClick={startVerify} disabled={busy} className="px-4 py-2 rounded-full bg-brand-orange text-white font-body text-sm font-semibold hover:bg-highlight-orange transition-colors disabled:opacity-60" data-testid="claim-start-verify">{busy ? 'Sending…' : method === 'email' ? 'Send verification email →' : 'Continue to upload →'}</button>}
                {claim?.verification_status === 'awaiting_code' && <button onClick={submitCode} disabled={busy || code.length !== 6} className="px-4 py-2 rounded-full bg-forest text-white font-body text-sm font-semibold hover:opacity-90 disabled:opacity-50" data-testid="claim-submit-code">{busy ? 'Verifying…' : 'Verify code →'}</button>}
                {claim?.verification_status === 'awaiting_document' && <button onClick={submitDoc} disabled={busy || !file} className="px-4 py-2 rounded-full bg-forest text-white font-body text-sm font-semibold hover:opacity-90 disabled:opacity-50" data-testid="claim-submit-doc">{busy ? 'Uploading…' : 'Submit document →'}</button>}
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-5" data-testid="claim-step-3">
              <div className="rounded-[14px] p-4 bg-forest-light border border-[#A7F3D0] flex items-start gap-3">
                <span className="w-8 h-8 rounded-full bg-forest text-white flex items-center justify-center shrink-0"><Check className="w-4 h-4" strokeWidth={3} /></span>
                <div>
                  <p className="font-display text-base font-bold text-forest">Profile claimed successfully</p>
                  <p className="font-body text-xs text-ink-2">{biz.company_name} · You are now the profile owner{biz.claim_status === 'verified' ? ' · Verified ✓' : ' · Verification pending review by the Adoptic team'}</p>
                </div>
              </div>
              <p className="font-body text-[15px] text-ink-2 leading-relaxed">Your current BDAF score is <b className="text-ink">{biz.bdaf_score}</b>. To rank higher in Adoptic search and be discovered by more customers, complete the signals below — each one adds one point to your score at the next BDAF scan.</p>
              <div className="divide-y divide-warm-border rounded-[14px] border border-warm-border overflow-hidden">
                {ENRICH.map(e => {
                  const sig = (biz.signals || []).find(s => s.key === e.key);
                  const Icon = SIGNAL_ICONS[e.key];
                  return (
                    <div key={e.key} className="flex items-center gap-3 p-3 bg-white" data-testid={`enrich-${e.key}`}>
                      <span className={`w-8 h-8 rounded-[8px] flex items-center justify-center ${sig?.detected ? 'bg-forest-light text-forest' : 'bg-bg-warm text-ink-2'}`}><Icon className="w-4 h-4" /></span>
                      <span className="flex-1 font-body text-sm font-medium text-ink">{e.label}</span>
                      {sig?.detected ? <span className="font-body text-xs text-forest font-semibold">Detected ✓</span> : <><span className="font-body text-xs text-mid">+1 rank point</span><Link to="/my-business?tab=signals" className="px-3 py-1 rounded-full bg-brand-orange text-white font-body text-xs font-semibold hover:bg-highlight-orange">Add →</Link></>}
                    </div>
                  );
                })}
              </div>
              <div className="flex items-center justify-between pt-2 flex-wrap gap-3">
                <BDAFDots score={biz.bdaf_score} size={10} showLabel />
                <Link to="/my-business" className="px-4 py-2 rounded-full bg-ink text-white font-body text-sm font-semibold hover:opacity-90 inline-flex items-center gap-1" data-testid="claim-go-dashboard">Go to my dashboard <ArrowRight className="w-3.5 h-3.5" /></Link>
              </div>
            </div>
          )}
        </div>
        <p className="font-body text-xs text-mid text-center">Claims are free. Adoptic never changes your Companies House record.</p>
      </main>
    </div>
  );
}
